import { useTranslation } from 'react-i18next';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FolderPlusIcon } from 'lucide-react';

import { saveAnalysisAsArtifact } from '@/features/analysis';
import { SaveToProjectPopover } from '@/shared/ui';

interface AnalysisAddToProjectButtonProps {
  analysisId: string;
  /** Workspace to mint the artifact in; the section layout owns the gating that resolves it. */
  workspaceId: string;
}

/**
 * Attaches the analysis to one or more projects. Sits beside the per-section
 * "Download report" action, but the artifact it mints covers the whole analysis,
 * not the section on screen — re-saving from any section refreshes the same row.
 */
export const AnalysisAddToProjectButton = ({
  analysisId,
  workspaceId,
}: AnalysisAddToProjectButtonProps) => {
  const { t } = useTranslation('analysis');
  const label = t('analysisResult.analysisAddToProjectButton.addToProject');

  return (
    <TooltipProvider>
      <Tooltip>
        <SaveToProjectPopover
          resourceId={analysisId}
          workspaceId={workspaceId}
          onMintArtifact={() => saveAnalysisAsArtifact(analysisId, workspaceId)}
        >
          {/* Tooltip trigger and popover trigger have to compose onto the one
              button: both use `asChild`, so wrapping the popover instead would
              hang the tooltip on its menu items too. */}
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={label}>
              <FolderPlusIcon className="size-4" />
            </Button>
          </TooltipTrigger>
        </SaveToProjectPopover>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
